import * as React from "react";

import * as R from "ramda";

import * as classNames from "classnames";
import { renderToStaticMarkup } from 'react-dom/server';

import { backgroundStyleFromNode } from "../../../helpers/Doc/changes";
import { displayModes } from "../../../helpers/Doc/displayMode";
import { currentTimeInSeconds } from "../../../helpers/time_format";

import DocCDQFeatures from "./CDQFeatures";
import DocSectionHeader from "./Section/Header";

import DocBlockNodes from "../BlockNodes";
import IntegrationsTags from "../../Integrations/component";

import DocFeedbackModal from "../Modals/FeedbackModal";
import LowTimeIndicator from "images/low-time-indicator.png";
import GreenCheckImage from "images/completed-check.png";
import Confetti from 'react-confetti';
interface DocSectionProps extends DocSectionType<InlineNode>, BlockNodeAll {
  blockNodesContainsSelection?: boolean;
  currentMenu?: DocMenu;
  integrationInfo: IntegrationInfo;
  openSiblingSectionUIDs: Array<string>;
  paused?: boolean;
  roData: RoData;
  roDocumentCount: number;
  submitted?: boolean;
};

interface DocSectionState {
  timeLeftInSeconds?: number;
  showModal: boolean;
  lowTimeModal: boolean;
  completionModal: boolean;
};

export default class DocSection extends React.PureComponent<DocSectionProps, DocSectionState> {
  constructor(props: DocSectionProps) {
    super(props);
    let startingState: DocSectionState = {
      showModal: localStorage.getItem("showModal") === "true",
      lowTimeModal: localStorage.getItem("lowTimeModal") === "true",
      completionModal: localStorage.getItem("completionModal") === "true",
    };

    const timeLeft = this.calculateTimeLeft();
    if (timeLeft) {
      startingState = {...startingState, timeLeftInSeconds: timeLeft};
    }

    this.state = startingState;
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleAcknowledgeLowTime = this.handleAcknowledgeLowTime.bind(this);
  }

  private sectionRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
  private timer?: ReturnType<typeof setTimeout>;
  private oldBoundingClientRect?: DOMRect;

  componentDidMount() {
    if (!this.props.userContext.isAdmin) { this.startTimer(); }
    document.addEventListener("acknowledgeLowTime", this.handleAcknowledgeLowTime);
  }

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
    }
    document.removeEventListener("acknowledgeLowTime", this.handleAcknowledgeLowTime);
  }

  startTimer() {
    if (this.props.timeLimit) {
      if (!this.props.currentTimeSegmentStart && this.props.timeUsedBeforeCurrentSegment && this.props.timeUsedBeforeCurrentSegment>0) {
        this.props.userContext.addState({
          [this.props.uid]: {currentTimeSegmentStart: Math.floor(new Date().getTime() / 1000)},
        });
      }

      this.timer = setInterval(this.tick.bind(this), 1000);
    }
  }

  calculateTimeLeft() {
    if (this.props.timeLimit) {
      let timeLeft = this.props.timeLimit;
      if (this.props.timeUsedBeforeCurrentSegment) {
        timeLeft = timeLeft - this.props.timeUsedBeforeCurrentSegment;
      }

      if (this.props.currentTimeSegmentStart) {
        timeLeft = timeLeft - (currentTimeInSeconds() - this.props.currentTimeSegmentStart);
      }

      return timeLeft;
    } else {
      return;
    }
  }

  tick() {
    if ( this.props.timeLimit && this.props.currentTimeSegmentStart ) {
      this.setState({timeLeftInSeconds: this.calculateTimeLeft()});
    }
  }

  componentWillUpdate(nextProps: DocSectionProps, nextState: DocSectionState) {
    if (this.props.collapsed !== nextProps.collapsed) {
      this.oldBoundingClientRect = this.sectionRef.current?.getBoundingClientRect();
    }
  }

  componentDidUpdate(prevProps: DocSectionProps, prevState: DocSectionState) {
    if ((prevProps.collapsed !== this.props.collapsed) && this.props.collapsed) {
      const oldBoundingClientRect = this.oldBoundingClientRect;
      const newBoundingClientRect = this.sectionRef.current?.getBoundingClientRect();
      if (oldBoundingClientRect && newBoundingClientRect) {
        if (oldBoundingClientRect.bottom < 0) {
          window.scrollBy(0,newBoundingClientRect.height - oldBoundingClientRect.height);
        } else if (oldBoundingClientRect.top < 0) {
          if (this.props.collapsed) {
            window.scrollBy(0,newBoundingClientRect.bottom - 70);
          }
        }
      }
    }
  }

  // Function to update states
  handleShowModal = () => {
    const percentElement = document.getElementById('percentRecommendedTimeField') as HTMLInputElement;
    const percentOfRecommendedTime = parseFloat(percentElement?.value || "100");

    if (percentOfRecommendedTime < 25) {
      localStorage.setItem("lowTimeModal", "true");
      this.setState({
        lowTimeModal: true,
      });
    } else {
      localStorage.setItem("completionModal", "true");
      localStorage.removeItem("showModal");
      localStorage.removeItem("lowTimeModal");
      this.setState({
        completionModal: true,
        showModal: false,
        lowTimeModal: false,
      });

      setTimeout(() => {
        document.dispatchEvent(new Event('acknowledgeLowTime'));
      }, 5000);
    }
  };

  handleAcknowledgeLowTime = () => {
    localStorage.removeItem("lowTimeModal");
    localStorage.setItem("showModal", "true");
    localStorage.removeItem("completionModal");
    this.setState({
      lowTimeModal: false,
      showModal: true,
      completionModal: false,
    });
  };

  render() {
    const {
      title,
      displayContext,
      userContext,
      content,
    } = this.props;

    const isFeedbackCard = this.props.classes?.includes("feedback_card");
    if (isFeedbackCard && displayContext.displayMode === displayModes.STUDENT) {
      const button = content.find((child) => child.type == "button")

      return <DocFeedbackModal
        displayContext={displayContext}
        userContext={userContext}
        onClick={button.onClick}
        onSubmitFinish={this.handleShowModal}
      />
    }

    if (!this.props.userContext.isAdmin) {
      if (this.props.submitted) {
        clearInterval(this.timer);
        this.props.userContext.submitExam("Your exam has been sucessfully submitted.");
        return null;
      } else if (this.props.timeLimit && this.state.timeLeftInSeconds && this.state.timeLeftInSeconds <= 0) {
        clearInterval(this.timer);
        this.props.userContext.submitExam("Your time has expired. Your exam has been sucessfully submitted.");
        return null;
      } else if (this.props.paused) {
        this.props.userContext.pauseExam();
        return null;
      }
    }

    let visibleContent = this.props.content;
    let shouldDisplayIntegrationTags = this.props.classes?.includes("case_synopsis_card") && this.props.featureFlag.integrations

    if (!this.props.displayContext.isDisplayFull) {
      if ((this.props.level !== 0 && !this.props.nested) || (this.props.collapsed) || (this.props.inactiveBool)) {
        visibleContent = [];
        shouldDisplayIntegrationTags = false;
      }
    }

    if (!this.props.displayContext.isUngated) {
      visibleContent = visibleContent.filter((node) => node && node.invisible !== true);
    }

    if (this.props.displayContext.onlyDisplaySectionTitles) {
      visibleContent = visibleContent.filter((node) => node.type === "section");
      shouldDisplayIntegrationTags = false;
    }

    let openSiblingSectionUIDs;
    if (this.props.level === 0 && !this.props.displayContext.canBeEditing) {
      openSiblingSectionUIDs = visibleContent.filter((node) => {
        return (
          node.type === "section"
          && node.collapsed !== true
          && node.collapsible === true
        );
      }).map((node) => node.uid);
    }

    const classes = classNames("doc-section", this.props.uid, this.props.classes,
      {
        nested: this.props.nested,
        collapsible: this.props.collapsible,
        collapsed: this.props.collapsed,
        inactive: this.props.inactiveBool,
        "focused-bookmark": this.props.isFocusedBookmark,
      });

    const isSummarySatementSection = typeof title === "string" && /^Summary statement/i.test(title);
    const isSaqCard = this.props.classes?.includes("saq_card");
    const isFinishCaseCard = this.props.classes?.includes("finish_case_card");
    let propsClone = {...this.props};

    if (displayContext.enableAiSummaryStatementFeedback && isSummarySatementSection) {
      const questionSectionIndex = visibleContent.findIndex((node) => node.type == "section" && node.classes?.includes("question"));
      const questionSection = visibleContent.find((node): node is DocSectionType<InlineNode> => node.type === "section" && !!node.classes?.includes("question"))

      const answerCommentIndex = questionSection?.content?.findIndex((node): node is DocSectionType<InlineNode> => node.type === "section" && !!node.classes?.includes("answer_comment"));
      const answerCommentSection = questionSection?.content?.find((node): node is DocSectionType<InlineNode> => node.type === "section" && !!node.classes?.includes("answer_comment"));
      const idealSummaryStatement = answerCommentSection?.content?.find((node): node is DocParagraphType<InlineNode> => node.type === "paragraph" && !(/<strong>Expert Answer:<\/strong>/.test(node.content)))?.content;
      const summaryStatementTextInput = questionSection?.content?.find((node): node is DocTextInputType => node.type === "textInput");
      const summaryStatementSubmitButtonIndex = questionSection?.content?.findIndex((node): node is DocButtonType<InlineNode> => node.type === "button");
      const summaryStatementSubmitButton = questionSection?.content?.find((node): node is DocButtonType<InlineNode> => node.type === "button");

      if (idealSummaryStatement && summaryStatementTextInput && summaryStatementSubmitButton) {
        const summaryStatementSubmitButtonActions = summaryStatementSubmitButton.onClick;

        visibleContent = R.over(R.lensPath([questionSectionIndex, "content"]), R.remove(answerCommentIndex, 1), visibleContent);
        visibleContent = R.over(R.lensPath([questionSectionIndex, "content"]), R.remove(summaryStatementSubmitButtonIndex, 1), visibleContent);

        summaryStatementTextInput.aiAnswerCommentSection = answerCommentSection;
        summaryStatementTextInput.aiSummaryStatementIdeal = idealSummaryStatement;
        summaryStatementTextInput.aiSummaryStatementAllowFeedback = true;
        summaryStatementTextInput.aiSubmitButtonActions = summaryStatementSubmitButtonActions;
      }
    }

    if(isSaqCard) {
      return <></>;
    }

    /*
     * FIXME: This is an absolute mess and unsustainable.
     * Cleanup is required before any more work is done here.
     * ------------------------- BEGIN -----------------------------------
    */
    if(isFinishCaseCard) {
      const quizContent = !this.props.documentContext.quizUrl ? '' : `
        <div class="quiz-buttons">
          ${this.props.documentContext.quizUrl.includes('create_start_quiz') ? `
            <a class="finish-card-button" href=${this.props.documentContext.quizUrl} target="_blank" aria-label="Take the quiz now" > <span class="finish-card-quiz-text" data-url=${this.props.documentContext.quizUrl}> Take the quiz now </span> </a>
          ` : `
            <a class="finish-card-button"
              href="#"
              data-url=${this.props.documentContext.quizUrl}
              role="button"
              aria-label="Take the quiz now"
              onclick="javascript: event.preventDefault();
                                        fetch(event.target.dataset.url)
                                          .then(response => response.text())
                                          .then(html => Turbo.renderStreamMessage(html))
                                          .then(() => {
                                              document.getElementById('openQuestionContainer').classList.remove('hidden');
                                              analytics.track('open_questions', { page: window.location.href, source: 'Case Page', case_name: '${this.props.documentContext.title}', time_on_case: '${this.props.documentContext.timeOnCase}' });
                                          });"
            >
              <span class="finish-card-quiz-text" data-url=${this.props.documentContext.quizUrl}> Take the quiz now </span>
            </a>
          ` }
          ${ !this.props.documentContext.todoUrl ? '' : `
            <a href="#"
              data-url=${this.props.documentContext.todoUrl}
              role="button"
              aria-label="Save quiz to do list"
              onclick="javascript: event.preventDefault();
                                    fetch(event.target.dataset.url)
                                    .then(response => response.text())
                                    .then(html => Turbo.renderStreamMessage(html))
                                    .then(() => {
                                        document.getElementById('todo_form_container').classList.remove('hidden');
                                        analytics.track('saq_save_to_do', { page: window.location.href, source: 'Case Page', case_name: '${this.props.documentContext.title}', time_on_case: '${this.props.documentContext.timeOnCase}' });
                                    });"
              class="finish-card-todo-button"
              id="todoURLButton"
              >
              ${this.props.documentContext.todoAction}
            </a>
          `}
        </div>
        <br>
      `;

      const content = `
        <div class="finish-card">
          <div class='finish-card-start'>
            Well done! You have completed the case.
          </div>
          <div class="finish-card-layout">
            ${this.props.documentContext.quizUrl ? `
            <div class="finish-card-quiz-body">
              <div class='finish-card-body'>
                <h5 class='finish-card-heading'> Next: Check your knowledge </h5>
                <div class='finish-card-question-icon'></div>
                <p class='finish-card-content'> Take a PracticeSmart quiz with ${this.props.documentContext.quizQuestionCount} questions related to this case. </p>
                <p class='finish-card-content'> <b> Short on time? </b> Save the quiz to your to-do list for later.</p>
              </div>
              ${quizContent}
            </div>
            ` : ''}
            <div class='finish-card-summary-body'>
              <h5 class='finish-card-heading'> ACCESS CASE SUMMARY </h5>
              <div class='finish-card-summary-icon'></div>
              <p class='finish-card-content'> Download a printable Case Summary of the key teaching points covered in this case. </p>
            </div>
            <a class="finish-card-summary-button" href=${this.props.documentContext.caseSummaryUrl} target="_blank" onclick="analytics.track('view_case_summary', { page: window.location.href, source: 'Case Page', case_name: '${this.props.documentContext.title}', time_on_case: '${this.props.documentContext.timeOnCase}' });localStorage.removeItem('showModal')"> View Case Summary <span class="view-summary-icon"></span> </a>
          </div>
        </div>
        <div class="gen-modal hidden" id="openQuestionContainer">
          <div class="gen-modal-panel" style="padding-top: 33px; padding-left: 51px; padding-right: 63px; font-family: roboto; font-size: 16px;width: 498px;">
            <div id="quizModal"></div>
          </div>
        </div>
      `;

      // Modal content (same as original content but with a different UI)
      const modalContent = `
        <div data-controller="modal">
          <div class="gen-modal" data-modal-target="modal" id="finish_card_modal" role="dialog" aria-labelledby="finish_card_heading" aria-describedby="finish_card_body">
            <div class="gen-modal-panel">
              <div class="gen-modal-header-right">
                <button class="close-icon"aria-label="Close modal" data-action="click->modal#close">x</button>
              </div>
              <div class="finish-card">
                ${this.props.documentContext.quizUrl ? `
                <div class="finish-card-image-container">
                  <img alt="Quiztime" class="finish-card-image" aria-hidden="true" />
                </div>
                ` : ''}
                <div class="finish-card-layout">
                  ${this.props.documentContext.quizUrl ? `
                  <div class="finish-card-quiz-body">
                    <div class='finish-card-body'>
                      <h5 class='finish-card-heading'> Next: Check your knowledge </h5>
                      <p class='finish-card-content'> Take a PracticeSmart quiz with ${this.props.documentContext.quizQuestionCount} questions related to this case. </p>
                      <p class='finish-card-content'> <b> Short on time? </b> Save the quiz to your to-do list for later.</p>
                    </div>
                    ${quizContent}
                  </div>
                  ` : ''}
                  <div class='finish-card-summary-body'>
                    <h5 class='finish-card-heading'> ACCESS CASE SUMMARY </h5>
                    <p class='finish-card-content'> Download a printable Case Summary of the key teaching points covered in this case. </p>
                  </div>
                  <a class="finish-card-summary-button" href=${this.props.documentContext.caseSummaryUrl} target="_blank" aria-label="View case summary" onclick="analytics.track('view_case_summary', { page: window.location.href, source: 'Case Page', case_name: '${this.props.documentContext.title}', time_on_case: '${this.props.documentContext.timeOnCase}' });" autofocus> View case summary <span class="view-summary-icon"></span></a>
                </div>
              </div>
              <div class="gen-modal-header-right">
                <button class="close-btn" aria-label="Close modal" onclick="localStorage.removeItem('showModal')" data-action="click->modal#close"> Close </span>
              </div>
            </div>
          </div>
        </div>
      `;

      const lowTimeModal = `
        <div class="gen-modal">
          <div style="
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background-color: white;
            padding: 20px;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            z-index: 1000;
            max-width: 500px;
            text-align: center;
            border-radius: 8px;
          ">
            <div style="font-family: 'Roboto Condensed'; font-size: 24px; font-weight: 700; margin-bottom: 10px;">
              Case completed with low time
            </div>
            <div style="font-family: 'Roboto Condensed'; font-size: 20px; font-weight: 400; margin-bottom: 10px;">
              We noticed you completed the case in under 25% of the recommended time.
            </div>
            <div style="display: flex; justify-content: center; margin-bottom: 10px;">
              <img src="${LowTimeIndicator}" alt="low time indicator" />
            </div>
            <div style="font-family: 'Roboto Condensed'; font-size: 16px; font-weight: 400; color: red; margin-bottom: 5px;">
              Faculty can see this status on their reports.
            </div>
            <div style="font-family: 'Roboto Condensed'; font-size: 16px; font-weight: 400; margin-bottom: 20px;">
              <a href="https://aquifer.org/resources-tools/reporting-for-students/#1748451003797-741fdebd-489b" target="_blank" rel="noopener noreferrer">Learn more </a> about how time is calculated.
            </div>
            <div style="display: flex; justify-content: space-between; gap: 10px;">
              <a
                class="ref-btn"
                href="#"
                aria-label="Acknowledge low time"
                onclick="event.preventDefault();
                        document.dispatchEvent(new Event('acknowledgeLowTime'));"
              >
                <span>ok, I understand</span>
              </a>
              <a
                class="ref-btn"
                href="${this.props.displayContext.resetCaseUrl}"
                role="button"
                aria-label="reset case"
                data-method="post"
                data-confirm="Are you sure you want to reset this case? This will delete all of your work so you can go through the case again. Your previous work will not show in reports."
                onclick="
                        analytics.track('low_time_on_case_reset', {
                          user_id: '${this.props.userContext.id}',
                          program: '${this.props.displayContext.programName}',
                          time_on_case: '${this.props.documentContext.timeOnCase}',
                          page: '${this.props.displayContext.resetCaseUrl}',
                          source: 'Case Page'
                        }); localStorage.removeItem('lowTimeModal'); return true;"
              >
                <span>Reset case to restart my time</span>
              </a>
            </div>
          </div>
        </div>
      `;

    const completeCaseModal = renderToStaticMarkup(
      <div className="feedback-modal-background">
        <>
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            padding: '20px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            zIndex: 1000
          }}>
            <div style={{ fontFamily: "Roboto Condensed", fontSize: "24px", fontWeight: "700" }}>
              Excellent work!
            </div>
            <div style={{ fontFamily: "Roboto Condensed", fontSize: "24px", fontWeight: "400" }}>
              This case is now complete.
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img src={GreenCheckImage} alt="Green Check Mark" />
            </div>
          </div>
        </>
      </div>
    );

      // ... (modalContent and other rendering logic remain unchanged)
      let finalContent;
      if (this.state.lowTimeModal) {
        finalContent = `${lowTimeModal}`;
      } else if (this.state.completionModal) {
        finalContent = `${content} ${completeCaseModal}`;
      } else if (this.state.showModal) {
        finalContent = `${content} ${modalContent}`;
      } else {
        finalContent = `${content}`;
      }

      propsClone.title = 'Next Steps';
      if (propsClone.content[1]) {
        propsClone.content[1].content = finalContent;
      }
    }
    // ------------------------- END -------------------------------------

    return (
      <div
        id={this.props.uid}
        data-is-node
        data-uid={this.props.uid}
        className={classes}
        style={backgroundStyleFromNode(this.props)}
        contentEditable={false}
        ref={this.sectionRef}
      >
        <DocSectionHeader {...propsClone} />
        {this.state.completionModal && <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            style={{ position: 'fixed', top: 0, left: 0 }}
          />
         }
        <div className="doc-section-body">
          <DocCDQFeatures {...this.props} />
          <DocBlockNodes
            {...this.props}
            content={visibleContent}
            contentKey="content"
            independentlyEditable
            openSiblingSectionUIDs={openSiblingSectionUIDs}
            shouldHaveAddNodes={this.props.editingContext.isEditing && (this.props.displayContext.isDisplayFull || !this.props.collapsed)}
          />
          {shouldDisplayIntegrationTags &&
            <IntegrationsTags
              {...this.props.integrationInfo}
            />
          }
        </div>
      </div>
    );
  }
}
